import { useDialogFocus } from '../../hooks/useDialogFocus';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useCurrentVenue } from '../../hooks/useCurrentVenue';
import { useAuthStore } from '../../store/auth.store';

interface DisplayScreen {
  id: string;
  name: string;
  token: string;
  slug: string | null;
  tournamentId: string | null;
  layout: string;
  isActive: boolean;
}

interface Tournament {
  id: string;
  name: string;
  status: string;
}

const LAYOUT_KEYS: Record<string, string> = {
  TIMER_FULL: 'layoutTimerFull',
  BLIND_STRUCTURE: 'layoutBlindStructure',
  PRIZE_TABLE: 'layoutPrizeTable',
  LEADERBOARD: 'layoutLeaderboard',
};

interface Props {
  tournament: { id: string; name: string };
  onClose: () => void;
  onCreateScreen?: () => void;
}

export default function AssignTournamentModal({ tournament, onClose, onCreateScreen }: Props) {
  const { t } = useTranslation('display');
  const qc = useQueryClient();
  const { venue, error: venueError } = useCurrentVenue();
  const revision = useAuthStore((s) => s.authRevision);
  const {
    data: screens = [],
    isLoading,
    error: screensError,
  } = useQuery<DisplayScreen[]>({
    queryKey: ['display-screens', revision],
    enabled: Boolean(venue),
    queryFn: () => api.get<DisplayScreen[]>('/display/screens'),
  });

  const { data: tournaments = [] } = useQuery<Tournament[]>({
    queryKey: ['tournaments-active', revision],
    enabled: Boolean(venue),
    queryFn: () =>
      api
        .get<{ data: Tournament[]; total: number }>('/tournament?limit=50')
        .then((d) => d.data ?? []),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const assignToScreenMutation = useMutation({
    mutationFn: ({ screenId, tournamentId }: { screenId: string; tournamentId: string }) =>
      api.patch<DisplayScreen>(`/display/screens/${screenId}`, { tournamentId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['display-screens'] });
    },
  });

  const dialog = useDialogFocus(true, onClose, assignToScreenMutation.isPending);
  const error = venueError ?? screensError;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div
        ref={dialog}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-screen-title"
        className="bg-base-200 rounded-xl border border-base-300 p-4 sm:p-8 w-full max-w-lg max-h-[90dvh] overflow-y-auto shadow-2xl [&_button]:min-h-11"
      >
        <h2 id="assign-screen-title" className="text-xl font-bold mb-2">
          {t('assignTournamentToScreen')}
        </h2>
        <p className="text-sm text-base-content/50 mb-6">
          {t('tournament')}: {tournament.name}
        </p>
        {error ? (
          <p role="alert" className="text-error py-4">
            {error.message}
          </p>
        ) : isLoading || !venue ? (
          <p className="py-4">{t('loading')}</p>
        ) : screens.length === 0 ? (
          <div className="text-center py-8 text-base-content/40">
            <span className="material-symbols-outlined text-4xl block mb-2">tv_off</span>
            <p>{t('noAvailableScreens')}</p>
            {onCreateScreen && (
              <button
                onClick={onCreateScreen}
                className="mt-3 px-4 py-2 rounded-lg bg-primary text-black text-sm font-bold"
              >
                {t('createScreenBtn')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {screens.map((screen) => {
              const linked = tournaments.find((t) => t.id === screen.tournamentId);
              const isAssigned = screen.tournamentId === tournament.id;
              return (
                <div
                  key={screen.id}
                  className={`flex items-center justify-between gap-3 p-4 rounded-lg border transition-colors ${
                    isAssigned
                      ? 'border-emerald-500/40 bg-emerald-500/10'
                      : 'border-base-300/30 hover:border-primary/30'
                  }`}
                >
                  <div className="min-w-0 break-words">
                    <p className="font-bold text-sm">{screen.name}</p>
                    <p className="text-xs text-base-content/50">
                      {LAYOUT_KEYS[screen.layout]
                        ? t(LAYOUT_KEYS[screen.layout] as string)
                        : screen.layout}{' '}
                      · {linked ? t('currentBinding', { name: linked.name }) : t('notAssigned')}
                    </p>
                  </div>
                  {isAssigned ? (
                    <span className="shrink-0 text-xs text-emerald-400 font-bold flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">check_circle</span>
                      {t('assigned')}
                    </span>
                  ) : (
                    <button
                      onClick={() =>
                        assignToScreenMutation.mutate({
                          screenId: screen.id,
                          tournamentId: tournament.id,
                        })
                      }
                      disabled={assignToScreenMutation.isPending}
                      className="shrink-0 min-h-11 px-4 py-1.5 rounded-lg bg-primary text-black text-xs font-bold hover:bg-primary/90 disabled:opacity-50"
                    >
                      {assignToScreenMutation.isPending ? t('assigning') : t('assign')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {assignToScreenMutation.error && (
          <p role="alert" className="text-error mt-4">
            {assignToScreenMutation.error.message}
          </p>
        )}
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-base-content/50 hover:bg-base-300"
          >
            {t('close', { ns: 'common' })}
          </button>
        </div>
      </div>
    </div>
  );
}
